// ---------------------------------------------------------------------------
// DataState.tsx — shared honest-state presentational components.
//
// Audit rule (ledger_UI_deadwire.md W4/W5): a fetch/query failure must render
// a VISIBLE "data unavailable" state, never the empty/healthy default; and a
// status light must reflect data FRESHNESS, not row existence.
//
// Reference implementation these generalize: VelocityTriage's red error panel
// (the F-07 counter-example) and ReviewDevices' recency StatusDot (F-03 model).
// ---------------------------------------------------------------------------

import { AlertTriangle, Inbox } from 'lucide-react';
import type { Freshness, FreshnessResult } from '../lib/freshness';
import { resolveIndicatorColor, freshnessTitle } from '../lib/freshness';

// --- Error state: a backend/API failure. NEVER a healthy-looking default. ---
export function DataUnavailable({
  message,
  onRetry,
  compact = false,
}: {
  message?: string;
  onRetry?: () => void;
  compact?: boolean;
}) {
  return (
    <div
      role="alert"
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 text-red-700 ${
        compact ? 'py-4 px-3 text-xs' : 'py-8 px-4 text-sm'
      }`}
    >
      <AlertTriangle className={compact ? 'w-4 h-4' : 'w-6 h-6'} />
      <span className="text-center font-medium">{message || 'Data unavailable'}</span>
      {!compact && (
        <span className="text-center text-red-500 text-xs">
          This panel could not be loaded. It is not showing an all-clear — the check did not run.
        </span>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-1 px-3 py-1 rounded-md border border-red-300 bg-white text-red-600 text-xs hover:bg-red-100 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );
}

// --- Empty state: the query genuinely returned nothing (distinct from error). ---
export function NoData({
  message = 'No data',
  compact = false,
}: {
  message?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1.5 text-gray-400 ${
        compact ? 'py-4 text-xs' : 'py-8 text-sm'
      }`}
    >
      <Inbox className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
      <span className="text-center">{message}</span>
    </div>
  );
}

// --- Freshness-aware status dot: green only when fresh; gray/slashed else. ---
export function FreshnessDot({
  healthColor,
  freshness,
  size = 10,
  className = '',
}: {
  /** The health color to paint WHEN the data is fresh (hex or css color). */
  healthColor: string;
  /** Result of freshnessStatus(...) or a bare Freshness status. */
  freshness: FreshnessResult | Freshness;
  size?: number;
  className?: string;
}) {
  const status: Freshness =
    typeof freshness === 'string' ? freshness : freshness.status;
  const { color, slashed } = resolveIndicatorColor(healthColor, status);
  const title =
    typeof freshness === 'string' ? undefined : freshnessTitle(freshness);

  return (
    <span
      className={`relative inline-block shrink-0 rounded-full ${className}`}
      style={{ width: size, height: size, backgroundColor: color }}
      title={title}
      aria-label={title}
    >
      {slashed && (
        // Diagonal slash marks a disconnected sensor — unmistakable vs a solid dot.
        <span
          className="absolute inset-0"
          style={{
            background: `linear-gradient(45deg, transparent 44%, white 44%, white 56%, transparent 56%)`,
            borderRadius: '9999px',
          }}
        />
      )}
    </span>
  );
}
